/**
 * API response and error types for frontend-backend communication.
 * These types match the backend response format from packages/api.
 */

/**
 * Standard API response wrapper from backend.
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  meta: {
    correlationId: string;
    timestamp: string;
  };
}

/**
 * Custom error class for API errors.
 * Preserves correlation ID for debugging and support.
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public correlationId: string,
    public statusCode?: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Unwrap API response and throw ApiError on failure.
 * Use this in service methods to extract data from responses.
 */
export function unwrapResponse<T>(response: ApiResponse<T>): T {
  if (!response.success || response.data === undefined) {
    throw new ApiError(
      response.error?.code ?? 'UNKNOWN_ERROR',
      response.error?.message ?? 'An unknown error occurred',
      response.meta?.correlationId ?? 'unknown',
      undefined,
      response.error?.details
    );
  }
  return response.data;
}
